import { useContext, useLayoutEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { useUserPermissions } from "@/hooks/useUserPermissions"
import { Pencil, Search } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Spinner } from "@/components/ui/spinner"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { selectTabs, TAB_TYPES } from "@/reducers/tabsSlice"
import { selectAccountChatsAsList } from "@/reducers/accountChatsSlice"
import { selectChats } from "@/reducers/chatsSliceSelectors"
import { selectUserChats } from "@/reducers/userChatsSlice"
import { buildChat } from "@/reducers/chatsSlice"
import { joinChat, leaveChat } from "@/reducers/chatsSliceThunks"
import { RootState } from "@/reducers/store"
import { AccountChat } from "@/lib/accountChatsParser"
import ChatHelpers from "@/lib/chatHelpers"
import { addBookmark, removeBookmark } from "@/api/xeps/PepNativeBookmarks"
import { XmppContext } from "@/stream/xmppClient"
import { getCarIssuesJid, getSlickshiftAIJid } from "@/jidUtils"
import {
  findTranschatUserChat,
  isSlickshiftUser,
  shouldHideTranschatChat,
} from "@/lib/privateAgentUtils"
import { useTranslation } from "react-i18next"

const GROUP_NAMES_ORDER = [
  "Double Crew",
  "Drivers",
  "Shared Drivers",
  "Transics",
  "Whatsapp Contacts",
]

// Group names are ids from the chat parser; the known ones are shown
// translated, any other is shown as it comes.
const GROUP_NAME_KEYS = {
  Drivers: "fleet.myCars.groups.drivers",
  "Shared Drivers": "fleet.myCars.groups.sharedDrivers",
  "Whatsapp Contacts": "fleet.myCars.groups.whatsappContacts",
  "Double Crew": "fleet.myCars.groups.doubleCrew",
} as const

const isKnownGroupName = (
  groupName: string,
): groupName is keyof typeof GROUP_NAME_KEYS => groupName in GROUP_NAME_KEYS

type MyCarsRow = {
  chat: AccountChat
  carLabel: string | undefined
  canToggle: boolean
}

export function MyCars() {
  const { t } = useTranslation()
  const { client, myJid } = useContext(XmppContext)
  const dispatch = useAppDispatch()
  const { activeTab } = useAppSelector(selectTabs)
  const accountChats = useAppSelector(selectAccountChatsAsList)
  const chats = useAppSelector(selectChats)
  const userChats = useAppSelector(selectUserChats)
  const cars = useAppSelector((state: RootState) => state.cars)
  const userChatsEnabled = Boolean(useFeatureFlagEnabled("private-agent"))
  // On a managed account the fleet manager hands the cars out.
  const { membershipLocked } = useUserPermissions()

  const [isOpen, setIsOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState("")
  const [selectedJids, setSelectedJids] = useState<Set<string>>(new Set())

  useLayoutEffect(() => {
    setIsOpen(activeTab === TAB_TYPES.MY_CARS)
    if (activeTab !== TAB_TYPES.MY_CARS) {
      setIsEditing(false)
      setError(null)
      setFilter("")
    }
  }, [activeTab])

  const bookmarkedJids = useMemo(
    () => new Set(chats.map((chat) => chat.jid)),
    [chats],
  )

  const rows: MyCarsRow[] = useMemo(() => {
    const assistantChatJid = getSlickshiftAIJid(myJid)
    const carIssuesJid = getCarIssuesJid(myJid)
    const isSlickshiftLoggedUser = isSlickshiftUser(myJid.local)
    const ownTranschatJid = findTranschatUserChat(userChats, myJid.local)?.jid

    return accountChats
      .filter(
        (chat) =>
          chat.groupName !== "Groups" &&
          !chat.hidden &&
          chat.jid !== assistantChatJid &&
          chat.jid !== carIssuesJid &&
          (userChatsEnabled || !userChats[chat.jid]) &&
          !shouldHideTranschatChat({
            privateAgentEnabled: userChatsEnabled,
            isSlickshiftUser: isSlickshiftLoggedUser,
            chatJid: chat.jid,
            ownTranschatJid,
          }),
      )
      .map((chat) => {
        const car = chat.carId ? cars[chat.carId] : undefined
        return {
          chat,
          carLabel: car ? car.plates || car.label : undefined,
          canToggle: ChatHelpers.canUserLeaveChat(buildChat({ jid: chat.jid })),
        }
      })
      .sort((a, b) => a.chat.name.localeCompare(b.chat.name))
  }, [accountChats, cars, myJid, userChats, userChatsEnabled])

  const filteredRows = useMemo(() => {
    const query = filter.toLowerCase()
    if (!query) return rows
    return rows.filter(
      ({ chat, carLabel }) =>
        chat.name.toLowerCase().includes(query) ||
        (carLabel || "").toLowerCase().includes(query),
    )
  }, [rows, filter])

  const groupedRows = useMemo(() => {
    const groupNames = [
      ...new Set(
        GROUP_NAMES_ORDER.concat(filteredRows.map((row) => row.chat.groupName)),
      ),
    ]
    return groupNames
      .map((groupName) => ({
        groupName,
        rows: filteredRows.filter((row) => row.chat.groupName === groupName),
      }))
      .filter((group) => group.rows.length > 0)
  }, [filteredRows])

  const startEditing = () => {
    setSelectedJids(
      new Set(
        rows
          .filter(({ chat }) => bookmarkedJids.has(chat.jid))
          .map(({ chat }) => chat.jid),
      ),
    )
    setError(null)
    setIsEditing(true)
  }

  const cancelEditing = () => {
    setIsEditing(false)
    setError(null)
  }

  const toggleRow = (row: MyCarsRow, checked: boolean) => {
    if (!row.canToggle) return
    setSelectedJids((previous) => {
      const next = new Set(previous)
      if (checked) {
        next.add(row.chat.jid)
      } else {
        next.delete(row.chat.jid)
      }
      return next
    })
  }

  const saveChanges = async () => {
    const togglableRows = rows.filter((row) => row.canToggle)
    const toAdd = togglableRows.filter(
      ({ chat }) => selectedJids.has(chat.jid) && !bookmarkedJids.has(chat.jid),
    )
    const toRemove = togglableRows.filter(
      ({ chat }) => !selectedJids.has(chat.jid) && bookmarkedJids.has(chat.jid),
    )

    setIsSaving(true)
    setError(null)
    const failed: string[] = []

    for (const { chat } of toAdd) {
      try {
        await addBookmark(client, chat.jid, chat.name)
        window.analytics.track("AddBookmark", {
          chatJid: chat.jid,
          chatName: chat.name,
          from: "MyCars",
        })
        dispatch(joinChat(client, chat))
      } catch {
        failed.push(chat.name)
      }
    }

    for (const { chat } of toRemove) {
      try {
        await removeBookmark(client, chat.jid)
        window.analytics.track("RemoveBookmark", {
          chatJid: chat.jid,
          chatName: chat.name,
          from: "MyCars",
        })
        const storeChat = chats.find((c) => c.jid === chat.jid)
        if (storeChat) {
          dispatch(leaveChat(client, storeChat))
        }
      } catch {
        failed.push(chat.name)
      }
    }

    setIsSaving(false)
    if (failed.length > 0) {
      setError(t("fleet.myCars.updateFailed", { chats: failed.join(", ") }))
    } else {
      setIsEditing(false)
    }
  }

  const isRowChecked = (row: MyCarsRow) =>
    isEditing
      ? selectedJids.has(row.chat.jid)
      : bookmarkedJids.has(row.chat.jid)

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 left-[49px] z-50 flex w-[100vw-48px] items-center justify-center rounded-md bg-background">
      <div className="relative h-full w-full">
        <div className="h-full w-full overflow-auto">
          <div className="mx-auto max-w-3xl p-6">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold">
                  {t("fleet.myCars.title")}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t("fleet.myCars.subtitle")}
                </p>
              </div>
              {isEditing ? (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={cancelEditing}
                    disabled={isSaving}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    onClick={saveChanges}
                    disabled={isSaving}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {isSaving && (
                      <Spinner size="xs" className="mr-2 text-white" />
                    )}
                    {t("common.save")}
                  </Button>
                </div>
              ) : membershipLocked ? (
                <p className="text-sm text-muted-foreground">
                  {t("fleet.myCars.managedByFleetManager")}
                </p>
              ) : (
                <Button onClick={startEditing} variant="outline">
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("common.edit")}
                </Button>
              )}
            </div>

            {error && (
              <div className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t("fleet.myCars.filter")}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="pl-9"
              />
            </div>

            {groupedRows.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {t("fleet.myCars.empty")}
              </p>
            )}

            {groupedRows.map(({ groupName, rows: groupRows }) => (
              <div key={groupName} className="mb-4">
                <h2 className="mb-1 px-3 text-xs font-semibold uppercase text-muted-foreground">
                  {isKnownGroupName(groupName)
                    ? t(GROUP_NAME_KEYS[groupName])
                    : groupName}
                </h2>
                <div className="rounded-md border">
                  {groupRows.map((row) => (
                    <label
                      key={row.chat.jid}
                      className={`flex items-center gap-3 border-b px-3 py-2.5 last:border-b-0 ${
                        isEditing && row.canToggle
                          ? "cursor-pointer hover:bg-muted/50"
                          : ""
                      }`}
                    >
                      <Checkbox
                        checked={isRowChecked(row)}
                        disabled={!isEditing || !row.canToggle || isSaving}
                        onCheckedChange={(checked) =>
                          toggleRow(row, checked === true)
                        }
                      />
                      <span className="text-sm">{row.chat.name}</span>
                      {row.carLabel && row.carLabel !== row.chat.name && (
                        <span className="text-xs text-muted-foreground">
                          {row.carLabel}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default MyCars
